import ReactECharts from 'echarts-for-react';
import { useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingState } from '../common';
import { useEventContext } from '../../contexts/EventContext';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useSplitDistributionQuery } from '../../hooks/useSplitDistributionQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useGraphSplitsQuery } from '../../hooks/useGraphSplitsQuery';
import { useStoredRefreshInterval } from '../../hooks/useStoredRefreshInterval';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import type { SplitDistributionParticipant } from '../../services/graphApi';
import { createSplitDistributionOption } from './splitDistributionChartOptions';
import { StatisticsRefreshControl } from './StatisticsRefreshControl';

type SplitDistributionFilters = {
    gender: string;
    category: string;
    ageGroup: string;
    group: string;
    bibKeyword: string;
};

type ChartMouseEvent = {
    componentType?: string;
    dataIndex?: number;
};

type ChartDataZoomEvent = {
    start?: number;
    startValue?: number | string;
    batch?: Array<{
        start?: number;
        startValue?: number | string;
    }>;
};

const emptyFilters: SplitDistributionFilters = {
    gender: '',
    category: '',
    ageGroup: '',
    group: '',
    bibKeyword: '',
};

const refreshOptions = [
    { label: '수동', value: 0 },
    { label: '10초', value: 10000 },
    { label: '30초', value: 30000 },
    { label: '1분', value: 60000 },
];
const SPLIT_REFRESH_STORAGE_KEY = 'statistics.split.refreshIntervalMs';
const DEFAULT_VISIBLE_PARTICIPANT_COUNT = 121;
const MIN_VISIBLE_PARTICIPANT_COUNT = 20;
const MAX_VISIBLE_PARTICIPANT_COUNT = 300;
const VISIBLE_PARTICIPANT_STEP = 20;

function uniqueValues(values: Array<string | undefined>) {
    return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort(
        (firstValue, secondValue) => firstValue.localeCompare(secondValue, 'ko')
    );
}

function filterParticipants(
    participants: SplitDistributionParticipant[],
    filters: SplitDistributionFilters
) {
    const bibKeyword = filters.bibKeyword.trim();

    return participants.filter((participant) => {
        if (filters.gender && participant.gender !== filters.gender) {
            return false;
        }
        if (filters.category && participant.category !== filters.category) {
            return false;
        }
        if (filters.ageGroup && participant.ageGroup !== filters.ageGroup) {
            return false;
        }
        if (filters.group && participant.group !== filters.group) {
            return false;
        }
        if (bibKeyword && !String(participant.bibNumber).includes(bibKeyword)) {
            return false;
        }

        return true;
    });
}

function getNumericStartValue(value: number | string | undefined) {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? Math.max(Math.floor(numericValue), 0) : null;
}

function getDataZoomStartIndex(params: ChartDataZoomEvent, participantCount: number) {
    const payload = params.batch?.[0] ?? params;
    const startValue = getNumericStartValue(payload.startValue);

    if (startValue !== null) {
        return startValue;
    }

    if (typeof payload.start === 'number' && participantCount > 0) {
        return Math.max(Math.round(((participantCount - 1) * payload.start) / 100), 0);
    }

    return null;
}

function FilterSelect({
                          label,
                          value,
                          options,
                          disabled,
                          onChange,
                      }: Readonly<{
    label: string;
    value: string;
    options: string[];
    disabled?: boolean;
    onChange: (value: string) => void;
}>) {
    return (
        <select
            aria-label={label}
            className="split-filter-control"
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
        >
            <option value="">
                {label}
            </option>
            {options.map((option) => (
                <option value={option} key={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}

function SplitDistributionDashboard() {
    const { selectedEventId } = useEventContext();
    const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
    const collectBirth = collectFlagsQuery.data?.collectBirth ?? true;
    const collectGender = collectFlagsQuery.data?.collectGender ?? true;
    const [draftFilters, setDraftFilters] = useState<SplitDistributionFilters>(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState<SplitDistributionFilters>(emptyFilters);
    const [highlightedParticipantIndex, setHighlightedParticipantIndex] = useState<number | null>(null);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);
    const [visibleParticipantCount, setVisibleParticipantCount] = useState(DEFAULT_VISIBLE_PARTICIPANT_COUNT);
    const {
        applyRefreshInterval,
        draftRefreshIntervalMs,
        refreshIntervalMs,
        setDraftRefreshIntervalMs,
    } = useStoredRefreshInterval(SPLIT_REFRESH_STORAGE_KEY);
    const { data, error, isError, isFetching, isLoading, dataUpdatedAt, refetch } =
        useSplitDistributionQuery(selectedEventId, refreshIntervalMs);
    const categoryQuery = useGraphCategoriesQuery(selectedEventId);

    const participants = useMemo(() => data?.participants ?? [], [data?.participants]);
    const categories = useMemo(
        () => [...(categoryQuery.data ?? [])].sort((first, second) => second.id - first.id),
        [categoryQuery.data]
    );
    const appliedCategory = categories.find(
        (category) => category.categoryName === appliedFilters.category
    ) ?? null;
    const draftCategory = categories.find(
        (category) => category.categoryName === draftFilters.category
    ) ?? null;
    const splitQuery = useGraphSplitsQuery(
        selectedEventId,
        appliedCategory?.id ?? null,
        appliedCategory?.categoryName
    );

    const filterOptions = useMemo(() => ({
        genders: uniqueValues(participants.map((participant) => participant.gender)),
        categories: categories.map((category) => category.categoryName),
        ageGroups: uniqueValues(participants.map((participant) => participant.ageGroup)),
        groups: uniqueValues(draftCategory?.groups?.map((group) => group.groupName) ?? []),
    }), [participants, categories, draftCategory?.groups]);
    // 백엔드 응답에 남아있는 값과 무관하게, 미수집 항목은 필터를 무조건 전체로 고정한다.
    const effectiveAppliedFilters = useMemo(() => ({
        ...appliedFilters,
        ageGroup: collectBirth ? appliedFilters.ageGroup : '',
        gender: collectGender ? appliedFilters.gender : '',
    }), [appliedFilters, collectBirth, collectGender]);
    const filteredParticipants = useMemo(
        () => filterParticipants(participants, effectiveAppliedFilters),
        [participants, effectiveAppliedFilters]
    );
    const activeSplits = useMemo(() => splitQuery.data ?? [], [splitQuery.data]);
    const maxVisibleParticipantCount = Math.min(
        MAX_VISIBLE_PARTICIPANT_COUNT,
        Math.max(filteredParticipants.length, MIN_VISIBLE_PARTICIPANT_COUNT)
    );
    const activeVisibleParticipantCount = Math.min(visibleParticipantCount, maxVisibleParticipantCount);
    const activeVisibleStartIndex = Math.min(
        visibleStartIndex,
        Math.max(filteredParticipants.length - activeVisibleParticipantCount, 0)
    );
    const chartOption = useMemo(
        () => createSplitDistributionOption(
            activeSplits,
            filteredParticipants,
            highlightedParticipantIndex,
            activeVisibleStartIndex,
            activeVisibleParticipantCount,
            appliedCategory?.id ?? 0
        ),
        [
            activeSplits,
            filteredParticipants,
            highlightedParticipantIndex,
            activeVisibleStartIndex,
            activeVisibleParticipantCount,
            appliedCategory?.id,
        ]
    );
    const chartEvents = useMemo(() => ({
        mouseover: (params: ChartMouseEvent) => {
            if (params.componentType !== 'series' || params.dataIndex == null) {
                return;
            }

            setHighlightedParticipantIndex(params.dataIndex);
        },
        mouseout: () => {
            setHighlightedParticipantIndex(null);
        },
        globalout: () => {
            setHighlightedParticipantIndex(null);
        },
        datazoom: (params: ChartDataZoomEvent) => {
            const nextVisibleStartIndex = getDataZoomStartIndex(params, filteredParticipants.length);

            if (nextVisibleStartIndex === null) {
                return;
            }

            setVisibleStartIndex((current) =>
                current === nextVisibleStartIndex ? current : nextVisibleStartIndex
            );
        },
    }), [filteredParticipants.length]);
    const chartHeight = 620;
    const hasChartData = activeSplits.length > 0
        && filteredParticipants.some((participant) =>
            participant.records.some((record) => record.chiptimeElapsedTime || record.guntimeElapsedTime)
        );
    const pageError = error ?? categoryQuery.error ?? splitQuery.error;
    const isPageError = isError || categoryQuery.isError || splitQuery.isError;
    const errorMessage = pageError instanceof Error
        ? pageError.message
        : '참가자 별 구간 통계 데이터를 불러오지 못했습니다.';

    const updateDraftFilter = (key: keyof SplitDistributionFilters, value: string) => {
        setDraftFilters((current) => ({ ...current, [key]: value }));
    };

    const applyFilterImmediately = (key: keyof SplitDistributionFilters, value: string) => {
        setDraftFilters((current) => ({ ...current, [key]: value }));
        setAppliedFilters((current) => ({ ...current, [key]: value }));
        setVisibleStartIndex(0);
    };

    const handleCategoryChange = (value: string) => {
        setDraftFilters((current) => ({ ...current, category: value, group: '' }));
        setAppliedFilters((current) => ({ ...current, category: value, group: '' }));
        setVisibleStartIndex(0);
    };

    const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
        event.preventDefault();
        setAppliedFilters((current) => ({ ...current, bibKeyword: draftFilters.bibKeyword }));
        setVisibleStartIndex(0);
        void refetch();
    };

    const handleResetFilters = () => {
        const resetFilters = {
            ...emptyFilters,
        };
        setDraftFilters(resetFilters);
        setAppliedFilters(resetFilters);
        setVisibleStartIndex(0);
    };

    const zoomInChartScope = () => {
        setVisibleParticipantCount((current) =>
            Math.max(MIN_VISIBLE_PARTICIPANT_COUNT, current - VISIBLE_PARTICIPANT_STEP)
        );
    };

    const zoomOutChartScope = () => {
        setVisibleParticipantCount((current) =>
            Math.min(maxVisibleParticipantCount, current + VISIBLE_PARTICIPANT_STEP)
        );
    };

    let chartContent: ReactNode = (
        <section className="split-chart-panel" aria-label="참가자 별 구간 통계">
            <div className="split-chart-scope-controls" aria-label="차트 표시 범위">
                <button
                    type="button"
                    aria-label="차트 확대"
                    title="차트 확대"
                    disabled={activeVisibleParticipantCount <= MIN_VISIBLE_PARTICIPANT_COUNT}
                    onClick={zoomInChartScope}
                >
                    +
                </button>
                <button
                    type="button"
                    aria-label="차트 축소"
                    title="차트 축소"
                    disabled={activeVisibleParticipantCount >= maxVisibleParticipantCount}
                    onClick={zoomOutChartScope}
                >
                    -
                </button>
            </div>
            <ReactECharts
                option={chartOption}
                notMerge
                style={{ height: chartHeight, width: '100%' }}
                onEvents={chartEvents}
            />
        </section>
    );

    if (selectedEventId == null) {
        chartContent = (
            <div className="split-distribution-message">
                선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
            </div>
        );
    } else if (isLoading || categoryQuery.isLoading || splitQuery.isLoading) {
        chartContent = <LoadingState message="참가자 별 구간 통계 데이터를 불러오는 중입니다." />;
    } else if (hasChartData === false) {
        chartContent = (
            <div className="split-distribution-message">표시할 참가자 별 구간 통계 데이터가 없습니다.</div>
        );
    }

    return (
        <div className="split-distribution-page">
            <div className="split-distribution-title-row">
                <div className="page-title-block">
                    <p className="page-section">Statistics</p>
                    <h1>참가자 별 구간 통계</h1>
                    <p className="page-description">구간별 참가자 통계를 실시간으로 확인합니다.</p>
                </div>
                <StatisticsRefreshControl
                    ariaLabel="실시간 갱신 주기"
                    className="split-refresh-control"
                    draftRefreshIntervalMs={draftRefreshIntervalMs}
                    isApplyDisabled={draftRefreshIntervalMs === refreshIntervalMs}
                    isRefreshDisabled={isFetching}
                    options={refreshOptions}
                    updatedAt={dataUpdatedAt}
                    onApply={applyRefreshInterval}
                    onChange={setDraftRefreshIntervalMs}
                    onRefresh={() => {
                        void Promise.all([
                            categoryQuery.refetch(),
                            splitQuery.refetch(),
                            refetch(),
                        ]);
                    }}
                />
            </div>

            <form className="statistics-filter-row statistics-filter-row--with-search" onSubmit={handleSubmit}>
                <FilterSelect
                    label="종목 전체"
                    value={draftFilters.category}
                    options={filterOptions.categories}
                    onChange={handleCategoryChange}
                />
                <FilterSelect
                    label="그룹"
                    value={draftFilters.group}
                    options={filterOptions.groups}
                    disabled={isFetching || !draftFilters.category}
                    onChange={(value) => applyFilterImmediately('group', value)}
                />
                <FilterSelect
                    label="나이그룹 전체"
                    value={collectBirth ? draftFilters.ageGroup : ''}
                    options={filterOptions.ageGroups}
                    disabled={!collectBirth || filterOptions.ageGroups.length === 0}
                    onChange={(value) => applyFilterImmediately('ageGroup', value)}
                />
                <FilterSelect
                    label="성별 전체"
                    value={collectGender ? draftFilters.gender : ''}
                    options={filterOptions.genders}
                    disabled={!collectGender || filterOptions.genders.length === 0}
                    onChange={(value) => applyFilterImmediately('gender', value)}
                />
                <input
                    className="split-filter-control split-bib-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="배번호를 입력하세요"
                    value={draftFilters.bibKeyword}
                    onChange={(event) => updateDraftFilter('bibKeyword', event.target.value)}
                />
                <button className="split-search-button" type="submit" disabled={isFetching}>
                    검색
                </button>
                <button
                    className="split-reset-button"
                    type="button"
                    disabled={isFetching}
                    onClick={handleResetFilters}
                >
                    {"\uCD08\uAE30\uD654"}
                </button>
            </form>

            {isPageError && (
                <div className="split-distribution-message split-distribution-error" role="alert">
                    {errorMessage}
                </div>
            )}

            {chartContent}
        </div>
    );
}

export default SplitDistributionDashboard;
